/**
 * Phone mutations: provision a number, set forwarding, set working hours.
 *
 * NON-OPTIMISTIC: phone provisioning and forwarding are provider configuration
 * (they shape live call routing). Each waits for the backend and invalidates the
 * number list / the affected number's config.
 */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { unwrap } from '../queries/_helpers';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type PhoneForwardingRule,
  type PhoneNumber,
  phoneForwardingRuleSchema,
  phoneNumberSchema,
  type WorkingHours,
  workingHoursSchema,
} from '../responses';
import type {
  ProvisionPhoneNumberBody,
  SetForwardingRuleBody,
  SetWorkingHoursBody,
} from './_types';

/** `POST /phone/numbers` — provision a phone number for the hotel. */
export function useProvisionPhoneNumber(
  tenantId: string,
  hotelId: string,
): UseMutationResult<PhoneNumber, unknown, ProvisionPhoneNumberBody> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ProvisionPhoneNumberBody) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/phone/numbers',
        { params: { path: { tenantId, hotelId } }, body, parseAs: 'json' },
      );
      return unwrap(result, phoneNumberSchema);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.phoneNumbers(tenantId, hotelId) });
    },
  });
}

/** `PUT /phone/forwarding` — set a forwarding rule for a number. */
export function useSetForwarding(
  tenantId: string,
  hotelId: string,
): UseMutationResult<PhoneForwardingRule, unknown, SetForwardingRuleBody> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetForwardingRuleBody) => {
      const result = await client.PUT(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/phone/forwarding',
        { params: { path: { tenantId, hotelId } }, body, parseAs: 'json' },
      );
      return unwrap(result, phoneForwardingRuleSchema);
    },
    onSuccess: (rule) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.phoneNumberConfig(tenantId, hotelId, rule.phoneNumberId),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.phoneNumbers(tenantId, hotelId) });
    },
  });
}

/** `PUT /phone/working-hours` — set working hours for one weekday. */
export function useSetWorkingHours(
  tenantId: string,
  hotelId: string,
): UseMutationResult<WorkingHours, unknown, SetWorkingHoursBody> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetWorkingHoursBody) => {
      const result = await client.PUT(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/phone/working-hours',
        { params: { path: { tenantId, hotelId } }, body, parseAs: 'json' },
      );
      return unwrap(result, workingHoursSchema);
    },
    onSuccess: () => {
      // Working hours belong to a number's config; refresh all number configs
      // for the hotel (the response does not carry the phoneNumberId).
      void queryClient.invalidateQueries({ queryKey: queryKeys.phoneNumbers(tenantId, hotelId) });
    },
  });
}
